import { resendOtpFormAction, verifyOtpFormAction } from '@/app/form-actions/auth';
import { setFormErrors } from '@/utils/form';
import { getRecaptchaToken } from '@/utils/recaptcha';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { useFormState, useFormStatus } from 'react-dom';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { FiArrowRight } from 'react-icons/fi';
import { CxButton } from '../atoms/CxButton';
import ErrorLabel from '../atoms/ErrorLabel';
import { OtpInputForm } from '../atoms/OtpInput';
import { motion } from 'motion/react';
import { bounceTransition } from '@/animation/transitions';

interface FormContentProps {
  form: UseFormReturn<FieldValues, any, any>;
  emailPreview: string;
  resendCountdown: number;
  onResend: () => void;
}

function FormContent({ form, emailPreview, resendCountdown, onResend }: FormContentProps) {
  const status = useFormStatus();
  const isResendDisabled = resendCountdown > 0;
  const otpValue = String(form.watch('otp') || '');
  const isVerifyDisabled = otpValue.length < 6;

  return (
    <>
      <div>
        <Image
          src={'/images/developed_by_itaicx.png'}
          alt="Delevolped by ItaiCX"
          width={140}
          height={140}
        />

        <span className="mt-10 flex flex-col gap-1 text-lg whitespace-nowrap">
          <h1 className="font-bold">Verificação</h1>
          <span className="text-sm text-gray-02">
            Enviamos um código de verificação para
            <span className="text-brand-color-green font-semibold"> {emailPreview}</span>.
          </span>
        </span>
      </div>

      <div className="mt-5 flex flex-col gap-4">
        <OtpInputForm name="otp" control={form.control} digits={6} />

        <p className="text-gray-02 text-sm">
          Não recebeu o código?{' '}
          <button
            type="button"
            onClick={onResend}
            disabled={isResendDisabled}
            className={`font-semibold ${
              isResendDisabled
                ? 'text-brand-color-green/60 cursor-default no-underline'
                : 'text-brand-color-green cursor-pointer underline'
            }`}
          >
            Reenviar{isResendDisabled ? ` (${resendCountdown}s)` : ''}
          </button>
        </p>
      </div>

      <div className="mt-4">
        <CxButton
          label="Verificar"
          isLoading={status.pending}
          disabled={isVerifyDisabled}
          icon={<FiArrowRight className='text-gray-02' />}
          type="submit"
        />
      </div>
    </>
  );
}

interface OtpAuthFormProps {
  emailPreview: string;
}

export function OtpAuthForm({ emailPreview }: OtpAuthFormProps) {
  const [actionState, formAction] = useFormState(verifyOtpFormAction, {});
  const [resendCountdown, setResendCountdown] = useState(59);
  const [resendFeedback, setResendFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const form = useForm();

  const onResend = useCallback(async () => {
    if (resendCountdown > 0) return;

    setResendCountdown(59);
    setResendFeedback(null);

    try {
      const token = await getRecaptchaToken('signin');
      const formData = new FormData();
      formData.append('recaptchaToken', token);
      const response = await resendOtpFormAction({}, formData);

      if (response?.error) {
        setResendFeedback({ type: 'error', message: response.error });
        setResendCountdown(0);
        return;
      }

      setResendFeedback({
        type: 'success',
        message: 'Código reenviado. Verifique sua caixa de entrada.',
      });
    } catch {
      setResendFeedback({ type: 'error', message: 'Não foi possível reenviar o código.' });
      setResendCountdown(0);
    }
  }, [resendCountdown]);

  useEffect(() => {
    setFormErrors(actionState?.fieldErrors as any, form as any);
  }, [actionState?.fieldErrors, form]);

  useEffect(() => {
    if (resendCountdown <= 0) return;

    const timer = setTimeout(() => {
      setResendCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCountdown]);

  return (
    <motion.form
      action={formAction}
      method="POST"
      initial={{ opacity: 0, translateX: 100 }}
      animate={{ opacity: 1, translateX: 0 }}
      exit={{ opacity: 0, translateX: -100 }}
      transition={bounceTransition}
      className="flex flex-col gap-4"
    >
      {actionState?.error && <ErrorLabel className="mb-4">{actionState?.error}</ErrorLabel>}
      {resendFeedback?.type === 'error' && (
        <ErrorLabel className="mb-4">{resendFeedback.message}</ErrorLabel>
      )}
      {resendFeedback?.type === 'success' && (
        <p className="text-brand-color-green mb-4 text-sm font-medium">{resendFeedback.message}</p>
      )}
      <FormContent
        form={form}
        emailPreview={emailPreview}
        resendCountdown={resendCountdown}
        onResend={onResend}
      />
    </motion.form>
  );
}
